import React, { useContext, useEffect, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import {
  Breadcrumbs,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Link
} from '@mui/material';
import Typography from '@mui/material/Typography';
import { doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { deleteFile, getMediaImage, uploadFile } from '../../../api/aws/s3';
import { articleSchema } from '../../../api/firebase/firestore/article/validation';
import {
  updateArticleInfo,
  mediasRef,
  updateDraftFlgFromMicroCms
} from '../../../api/firebase/firestore/media';
import { usersRef } from '../../../api/firebase/firestore/user';
import { getPost, postPosts, updatePost } from '../../../api/microcms/article';
import {
  gray,
  primaryButtonBackground,
  primarycolor,
  secondaryButtonnBackground
} from '../../../constants/Colors';
import {
  breadcrumb,
  buttonFont,
  mypageHeader,
  mypageHeaderTitle,
  mypageWrapper
} from '../../../constants/Location';
import { screenType } from '../../../constants/enum';
import { AuthContext } from '../../../contexts/auth';
import { throwError } from '../../../modules/error';
import CustomLoading from '../../uiParts/CustomLoading';
import CustomSnackbar from '../../uiParts/CustomSnackbar';
import RegisterArticleCard from '../../uiParts/RegisterArticleCard';
import Sidebar from '../../uiParts/Sidebar';

import type { RegisterArticleForm } from '../../uiParts/RegisterArticleCard';

const styles = {
  breadcrumb,
  breadcrumbsText: {
    color: gray,
    fontSize: '12px'
  },
  button: {
    '@media screen and (max-width:960px)': {
      height: '42px',
      width: '148px'
    },
    height: '42px',
    width: '192px'
  },
  buttonFont,
  buttonWrapper: {
    columnGap: '15px',
    display: 'flexbox',
    justifyContent: 'space-between'
  },
  headerButton: {
    ':hover': {
      border: 'none'
    },
    '@media screen and (max-width:480px)': {
      padding: 0
    },
    border: 'none'
  },
  modal: {
    '@media screen and (max-width:700px)': {
      padding: '24px 16px'
    },
    boxSizing: 'border-box',
    padding: '24px 40px',
    width: '100%'
  },
  modalDesc: {
    fontSize: '16px',
    whiteSpace: 'pre-wrap'
  },
  modalText: {
    fontSize: '22px',
    padding: 0
  },
  modalTitle: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  modalWrapper: {
    '& .MuiDialog-container': {
      '& .MuiPaper-root': {
        '@media screen and (max-width:700px)': {
          maxWidth: '343px'
        },
        margin: 0,
        maxWidth: '480px'
      },
      '@media screen and (max-width:700px)': {
        width: '343px'
      },
      margin: '0 auto',
      width: '676px'
    }
  },
  mypageHeader,
  mypageHeaderTitle,
  mypageWrapper,
  primaryButtonBackground,
  snackbar: {
    '@media screen and (max-width:960px)': {
      bottom: '24px',
      left: '20px',
      position: 'fixed',
      right: '20px'
    },
    bottom: '24px',
    display: 'block',
    left: '24px',
    position: 'absolute'
  }
};

interface MediaList {
  value: string;
  option: string;
}

const PublisherMypageArticleEdit: React.FC = () => {
  const { t } = useTranslation();
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [companyId, setCompanyId] = React.useState<string>('');
  const [mediaList, setMediaList] = React.useState<MediaList[]>([]);
  const { id } = useParams<{ id: string }>();
  const [mediaChangeDisabled, setMediaChangeDisabled] =
    React.useState<boolean>(false);
  const [draftSaveDisabled, setDraftSaveDisabled] =
    React.useState<boolean>(false);
  const [previewUnsaved, setPreviewUnsaved] = React.useState<boolean>(false);
  const [previewButtonText, setPreviewButtonText] = React.useState<string>(
    t('mypage.article.savePreview')
  );
  const [publishUnsaved, setPublishUnsaved] = React.useState<boolean>(false);
  const [editDisabled, setEditDisabled] = React.useState<boolean>(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [s3File, setS3File] = useState<FileList | null>(null);
  const [oldImage, setOldImage] = useState<string>('');
  const [firstSubmit, setFirstSubmit] = React.useState<boolean>(false);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [open, setOpen] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>('');
  const [infoText, setInfoText] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    document.title = t('title.publisher.articleRegisterEdit');
  }, []);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors }
  } = useForm<RegisterArticleForm>({
    defaultValues: {
      media: 0
    },
    resolver: yupResolver(articleSchema)
  });

  useEffect(() => {
    void (async () => {
      if (currentUser) {
        // 記事登録用にcompanyIdを取得
        const userRef = doc(usersRef, currentUser.uid);
        const userSnap = await getDoc(userRef);
        setCompanyId(userSnap.data()?.companyId as string);

        // メディア選択のリストを取得
        const q = query(
          mediasRef,
          where('companyId', '==', userSnap.data()?.companyId)
        );
        const querySnapshot = await getDocs(q);
        if (id) {
          const mediaData = querySnapshot.docs.map((document) => ({
            option: String(document.data().name),
            value: String(document.data().mediaId)
          }));
          setMediaList(mediaData);
        } else {
          const mediaData = querySnapshot.docs
            .filter((document) => !document.data().articleId)
            .map((document) => ({
              option: String(document.data().name),
              value: String(document.data().mediaId)
            }));
          setMediaList(mediaData);
        }
      }
    })();
  }, []);

  interface Article {
    contents: Array<{
      type: string;
      value: Array<{
        type: string;
        value: string;
      }>;
    }>;
  }

  useEffect(() => {
    void (async () => {
      setLoading(true);
      // 初期値設定 編集の場合とプレビューから戻る場合
      if (id) {
        // 記事内容取得
        const articleData = await getPost(id);

        // メディア情報取得
        const mediaRef = doc(mediasRef, articleData.mediaId);
        const mediaSnap = await getDoc(mediaRef);

        // dataを表示用に整形
        const outline = articleData.content
          ? (articleData.content as unknown as Article).contents[0]?.value.map(
              (item: { value: string }) => item.value
            )
          : undefined;
        const feature = articleData.feature
          ? (articleData.feature as unknown as Article).contents[0]?.value.map(
              (item: { value: string }) => item.value
            )
          : undefined;
        const menu = articleData.menu
          ? (articleData.menu as unknown as Article).contents[0]?.value.map(
              (item: { value: string }) => item.value
            )
          : undefined;
        const caseStudy = articleData.case
          ? (articleData.case as unknown as Article).contents[0]?.value.map(
              (item: { value: string }) => item.value
            )
          : undefined;
        const message = articleData.message
          ? (articleData.message as unknown as Article).contents[0]?.value.map(
              (item: { value: string }) => item.value
            )
          : undefined;

        const image = await getMediaImage(
          articleData.imagePath as unknown as string
        );

        setPreview(image);
        setOldImage(articleData.imagePath as string);

        reset({
          caseStudy: caseStudy !== undefined ? caseStudy.join('') : '',
          feature: feature !== undefined ? feature.join('') : '',
          media: articleData.mediaId,
          menu: menu !== undefined ? menu.join('') : '',
          message: message !== undefined ? message.join('') : '',
          outline: outline !== undefined ? outline.join('') : '',
          title: articleData.title
        });

        // メディア編集できないようにする
        setMediaChangeDisabled(true);

        if (
          id &&
          !mediaSnap.data()?.draftFlg &&
          mediaSnap.data()?.fromMediage
        ) {
          // 配信中の場合は下書き保存できないようにする
          setPreviewButtonText(t('mypage.article.publishedPreview'));
          setDraftSaveDisabled(true);
          setPreviewUnsaved(true);
        } else if (
          // 入稿がMicroCMSからの場合
          mediaSnap.data()?.fromMediage === undefined ||
          mediaSnap.data()?.fromMediage === false
        ) {
          setPreviewButtonText(t('mypage.article.preview'));
          setPreviewUnsaved(true);
          setDraftSaveDisabled(true);
          setEditDisabled(true);
          setPublishUnsaved(true);
        }
      }
      setLoading(false);
    })();
  }, []);

  const breadcrumbs = [
    <Link
      underline="hover"
      key="1"
      href="/main/publisher/mypage/article"
      sx={styles.breadcrumbsText}
    >
      {t('mypage.article.list')}
    </Link>,
    <Typography key="2" sx={styles.breadcrumbsText}>
      {id ? t('mypage.article.edit') : t('mypage.article.create')}
    </Typography>
  ];

  const changeUploadFile = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files;
    if (file) {
      const blob = new Blob([file[0]]);
      setPreview(window.URL.createObjectURL(blob));
      setS3File(file);
    }
  };

  const buttonDisabledFlag = (): boolean => {
    if (firstSubmit) {
      return (
        watch('media') === undefined ||
        0 ||
        watch('title') === undefined ||
        '' ||
        preview === null ||
        Boolean(errors.media) ||
        Boolean(errors.title) ||
        Boolean(errors.bgImg && (preview === undefined || preview === null)) ||
        Boolean(errors.outline) ||
        Boolean(errors.feature) ||
        Boolean(errors.menu) ||
        Boolean(errors.message) ||
        Boolean(errors.caseStudy)
      );
    }
    return (
      preview === null ||
      Boolean(errors.bgImg && (preview === undefined || preview === null))
    );
  };

  // 下書き保存とプレビュー時の保存
  const draftSave = async (type: string): Promise<void> => {
    try {
      if (type === 'preview' && previewUnsaved) {
        window.open(
          `/main/publisher/mypage/article/preview/${id ?? ''}`,
          '_blank'
        );
        return;
      }
      let filePath = '';
      let articleId = '';
      if (oldImage) {
        if (s3File) {
          filePath = `companies/${companyId}/article/${Date.now()}_${
            s3File[0].name
          }`;
          uploadFile(s3File, filePath);
          deleteFile(oldImage);
        } else {
          filePath = oldImage;
        }
      } else if (s3File) {
        filePath = `companies/${companyId}/article/${Date.now()}_${
          s3File[0].name
        }`;
        uploadFile(s3File, filePath);
      }
      // 編集時
      if (id) {
        const draftFlg = true;
        const response = (await updatePost(id, watch, companyId, filePath)) as {
          id: string;
        };
        articleId = response.id;
        // 記事を作成したら、firestoreのmediasにarticleIdを追加
        if (typeof watch('media') === 'string') {
          await updateArticleInfo(watch('media'), articleId, draftFlg);
        }
        // 新規作成時
      } else {
        const draftFlg = true;
        const response = (await postPosts(watch, companyId, filePath)) as {
          id: string;
        };
        articleId = response.id;
        if (typeof watch('media') === 'string') {
          await updateArticleInfo(watch('media'), articleId, draftFlg);
        }
      }

      if (type === 'preview') {
        navigate(`/publisher/mypage/article/preview/${id ?? articleId ?? ''}`);
      } else {
        setOpen(true);
        setInfoText(t('mypage.article.successDraft'));
        setTimeout(() => {
          navigate(`/publisher/mypage/article/edit/${id ?? articleId ?? ''}`);
        }, 2000);
      }
    } catch (e) {
      setOpen(true);
      setErrorText(t('mypage.article.error'));
      throwError(e);
    }
  };

  const onSubmit = async (): Promise<void> => {
    try {
      if (firstSubmit) {
        if (publishUnsaved) {
          await updateDraftFlgFromMicroCms(watch('media'));
          setModalOpen(false);
          setOpen(true);
          setInfoText(t('mypage.article.successRelease'));
          navigate(`/publisher/mypage/article/edit/${id ?? ''}`);
          return;
        }
        setLoading(true);
        let filePath = '';
        let articleId = '';
        if (oldImage) {
          if (s3File) {
            filePath = `companies/${companyId}/article/${Date.now()}_${
              s3File[0].name
            }`;
            uploadFile(s3File, filePath);
            deleteFile(oldImage);
          } else {
            filePath = oldImage;
          }
        } else if (s3File) {
          filePath = `companies/${companyId}/article/${Date.now()}_${
            s3File[0].name
          }`;
          uploadFile(s3File, filePath);
        }
        if (id) {
          const draftFlg = false;
          const response = (await updatePost(
            id,
            watch,
            companyId,
            filePath
          )) as {
            id: string;
          };
          articleId = response.id;
          if (typeof watch('media') === 'string') {
            await updateArticleInfo(watch('media'), response.id, draftFlg);
          }
        } else {
          const draftFlg = false;
          const response = (await postPosts(watch, companyId, filePath)) as {
            id: string;
          };
          articleId = response.id;
          // 記事を作成したら、firestoreのmediasにarticleIdを追加
          if (typeof watch('media') === 'string') {
            await updateArticleInfo(watch('media'), response.id, draftFlg);
          }
        }
        setModalOpen(false);
        setOpen(true);
        setInfoText(t('mypage.article.successRelease'));
        setLoading(false);
        setPreviewButtonText(t('mypage.article.publishedPreview'));
        setDraftSaveDisabled(true);
        setPreviewUnsaved(true);
        navigate(`/publisher/mypage/article/edit/${id ?? articleId ?? ''}`);
      }
    } catch (e) {
      setOpen(true);
      setErrorText(t('mypage.article.error'));
      throwError(e);
    }
  };

  const modalClose = (): void => {
    setModalOpen(false);
  };

  return (
    <>
      <CustomLoading loading={loading} />
      <Typography component="div" sx={styles.mypageWrapper}>
        <Sidebar screenType={screenType.publisher} />
        <Grid component="div" width="100%">
          <Typography
            sx={[
              styles.mypageHeader,
              {
                '@media screen and (max-width:390px)': {
                  alignItems: 'flex-start',
                  flexDirection: 'column',
                  height: '130px',
                  padding: '14px 16px 12px'
                },
                '@media screen and (min-width:391px) and (max-width:480px)': {
                  alignItems: 'flex-start',
                  flexDirection: 'column',
                  height: '95px',
                  padding: '14px 16px 12px'
                },
                '@media screen and (min-width:481px) and (max-width:600px)': {
                  height: '95px',
                  padding: '14px 16px 12px'
                }
              }
            ]}
            component="div"
          >
            <Typography sx={styles.mypageHeaderTitle}>
              {t('mypage.article.title')}
            </Typography>
            <Grid>
              <Grid
                container
                sx={{
                  '@media screen and (max-width:480px)': {
                    alignItems: 'flex-start',
                    flexDirection: 'row',
                    rowGap: '4px'
                  },
                  '@media screen and (min-width:481px) and (max-width:600px)': {
                    alignItems: 'flex-end',
                    flexDirection: 'column',
                    rowGap: '4px'
                  },
                  alignItems: 'center',
                  columnGap: '16px',
                  flexDirection: 'row'
                }}
              >
                <Typography
                  component="div"
                  sx={{
                    columnGap: '8px',
                    display: 'flex',
                    flexDirection: 'row'
                  }}
                >
                  <Button
                    variant="outlined"
                    color="error"
                    sx={styles.headerButton}
                    onClick={async () => {
                      await draftSave('draft');
                    }}
                    disabled={draftSaveDisabled}
                  >
                    {t('mypage.article.draft')}
                  </Button>
                  <Divider orientation="vertical" sx={{ height: '36px' }} />
                  <Button
                    variant="outlined"
                    color="error"
                    sx={styles.headerButton}
                    onClick={async () => {
                      await draftSave('preview');
                    }}
                  >
                    {previewButtonText}
                  </Button>
                </Typography>
                <Button
                  variant="contained"
                  sx={[
                    styles.primaryButtonBackground,
                    {
                      '@media screen and (max-width:480px)': {
                        height: 'unset',
                        width: '130px'
                      },
                      maxWidth: '192px'
                    }
                  ]}
                  onClick={async () => {
                    setFirstSubmit(true);
                    await handleSubmit(() => {
                      setModalOpen(true);
                    })();
                  }}
                  disabled={buttonDisabledFlag()}
                >
                  {t('mypage.article.toRelease')}
                </Button>
              </Grid>
            </Grid>
          </Typography>
          <Breadcrumbs
            separator=">"
            aria-label="breadcrumb"
            sx={styles.breadcrumb}
          >
            {breadcrumbs}
          </Breadcrumbs>
          <RegisterArticleCard
            control={control}
            watch={watch}
            errors={errors}
            mediaList={mediaList}
            mediaChangeDisabled={mediaChangeDisabled}
            changeUploadFile={changeUploadFile}
            preview={preview ?? ''}
            s3File={s3File}
            firstSubmit={firstSubmit}
            disabled={editDisabled}
          />
          <Dialog
            onClose={modalClose}
            open={modalOpen}
            sx={styles.modalWrapper}
          >
            <DialogContent sx={styles.modal}>
              <Typography component="div" sx={styles.modalTitle}>
                <DialogTitle sx={styles.modalText}>
                  {t('mypage.article.release')}
                </DialogTitle>
                <CloseIcon onClick={modalClose} sx={{ cursor: 'pointer' }} />
              </Typography>
              <Grid container justifyContent="center" sx={{ margin: '24px 0' }}>
                <Typography component="p" sx={styles.modalDesc}>
                  {t('mypage.article.releaseDesc')}
                </Typography>
              </Grid>
              <Grid
                container
                direction="row"
                alignItems="center"
                sx={styles.buttonWrapper}
              >
                <Button
                  variant="contained"
                  sx={[secondaryButtonnBackground, styles.button]}
                  onClick={() => {
                    modalClose();
                  }}
                >
                  <Typography
                    component="div"
                    color={primarycolor}
                    sx={styles.buttonFont}
                  >
                    {t('staff.cancel')}
                  </Typography>
                </Button>
                <Button
                  variant="contained"
                  sx={[primaryButtonBackground, styles.button]}
                  onClick={async () => {
                    await onSubmit();
                  }}
                >
                  <Typography
                    component="div"
                    color="white"
                    sx={styles.buttonFont}
                  >
                    {t('mypage.article.selectRelease')}
                  </Typography>
                </Button>
              </Grid>
            </DialogContent>
          </Dialog>
          <CustomSnackbar
            open={open}
            autoHideDuration={6000}
            onClose={() => setOpen(false)}
            text={errorText}
            infoText={infoText}
            sx={styles.snackbar}
          />
        </Grid>
      </Typography>
    </>
  );
};

export default PublisherMypageArticleEdit;
